import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import './App.css';
import { debouncedSaveCart, updateCartCustomerInfo, loadUserCart } from './utils/cartTracker';
import { migrateGuestHistoryToUser } from './utils/browsingHistory';
import { api } from './utils/api';
import { initSession, trackPageEnter, setupSessionLifecycle } from './utils/sessionTracker';
import { trackPageView, trackSearch, trackAddToCart } from './utils/analyticsTracker';
import TopBar from './components/TopBar';
import Header from './components/Header';
import Banner from './components/Banner';
import StatsStrip from './components/StatsStrip';
import CategoryGrid from './components/CategoryGrid';
import ProductSections from './components/ProductSections';
import SocialFeed from './components/SocialFeed';
import Sidebar from './components/Sidebar';
import ProductGrid from './components/ProductGrid';
import ImageSearchResults from './components/ImageSearchResults';
import Footer from './components/Footer';
import PromoBanner from './components/PromoBanner';
import FloatingButton from './components/FloatingButton';
import Notifications from './components/Notifications';
import LoginModal from './components/LoginModal';
import MyListModal from './components/MyListModal';
import ListToast from './components/ListToast';
import CompareBar from './components/CompareBar';
const CompareProducts = React.lazy(() => import('./pages/CompareProducts'));
import ShoppingCart from './components/ShoppingCart';
import CheckoutWrapper from './components/CheckoutWrapper';
import OrderConfirmationWrapper from './components/OrderConfirmationWrapper';
import InquiryConfirmedWrapper from './components/InquiryConfirmedWrapper';
import AvailabilityInquiryModal from './components/AvailabilityInquiryModal';
import FloatingReviewPopup from './components/FloatingReviewPopup';
const About = React.lazy(() => import('./pages/About'));
const PartnerWithUs = React.lazy(() => import('./pages/PartnerWithUs'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogArticle = React.lazy(() => import('./pages/BlogArticle'));
// import Delivery from './pages/Delivery'; // Commented out – delivery service not offered currently; uncomment when ready
const HelpCentre = React.lazy(() => import('./pages/HelpCentre'));
const ProductRequest = React.lazy(() => import('./pages/ProductRequest'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const AllDealsPage = React.lazy(() => import('./pages/AllDealsPage'));
const HotpointPriceMatchPage = React.lazy(() => import('./pages/HotpointPriceMatchPage'));
const EndingSoonPage = React.lazy(() => import('./pages/EndingSoonPage'));
const NewDealsPage = React.lazy(() => import('./pages/NewDealsPage'));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage'));
const Outlets = React.lazy(() => import('./pages/Outlets'));
const VendorProducts = React.lazy(() => import('./pages/VendorProducts'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const EmailUnsubscribe = React.lazy(() => import('./pages/EmailUnsubscribe'));
const MetaOEmbedDemo = React.lazy(() => import('./pages/MetaOEmbedDemo'));
import ApiDiagnostics from './components/ApiDiagnostics';
import ScrollToTop from './components/ScrollToTop';
import PageSeoManager from './components/PageSeoManager';
import { useData } from './context/DataContext';
import { StorefrontSettingsProvider, useStorefrontSettings } from './context/StorefrontSettingsContext';
import { analyzeImage, searchMatchingProducts, buildImageSearchCatalogContext } from './utils/imageAnalysis';
import { scrollToImageSearchView } from './utils/scrollToImageSearchView';
import { getParsedUser, persistAuthSession, clearAuthSession } from './utils/authStorage';
import { buildAvailabilityCheckUrl } from './utils/vendorAvailability';
import {
  recordAvailabilityInquiry,
  saveInquiryForConfirmation,
  fetchSurveyConsentStatus,
} from './utils/availabilityInquiry';
import {
  loadMyList,
  saveMyList,
  addProductToList,
  removeProductFromList,
  createListProductIdSet,
  getListProductId,
  parseSavedDealIdsParam,
  mergeSharedSavedDeals,
} from './utils/myList';
import { debouncedSyncSavedDeals } from './utils/savedDealsSync';
import {
  MAX_COMPARE_PRODUCTS,
  loadCompareList,
  saveCompareList,
  addProductsToCompare,
  removeProductFromCompare,
  createCompareProductIdSet,
  getCompareProductId,
  mergeCompareLists,
  buildCompareListFromIds,
  parseCompareIdsParam,
  shouldKeepCompareEmpty,
  clearCompareClearedFlag,
  markCompareCleared,
} from './utils/compareProducts';
import { debouncedSaveCompare, persistCompareNow, syncCompareWithAccount } from './utils/compareTracker';
import {
  filterProductsForPriceSlider,
  getCatalogMaxPrice,
  isPriceSliderActive
} from './utils/filterPrice';
import PlatformExperienceModal from './components/PlatformExperienceModal';
import {
  checkFeedbackEligible,
  consumePendingFeedbackSource,
  consumePendingFeedbackScore
} from './utils/platformFeedback';
import { captureReferralFromUrl } from './utils/referralTracking';
import { getSessionId } from './utils/sessionTracker';
import ReferralRedirect from './pages/ReferralRedirect';

const userInfoFromStorage = () => {
  const raw = getParsedUser();
  if (!raw) return null;
  const { token: _omit, ...rest } = raw;
  return rest;
};

// Set to false to re-enable the shopping cart on the website
const CART_DISABLED = true;

const SALES_HOLD_MESSAGE =
  'Online checkout is coming soon. Please speak with us for more details about this product.';

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    availabilityMessageTemplate,
    showInquiryOptIn,
  } = useStorefrontSettings();
  const { products, categories, subcategoriesByCategory, initialCatalogLoaded } = useData();
  const [allProductsConfig, setAllProductsConfig] = useState({ displayCount: 20, columnsPerRow: null });
  const [filters, setFilters] = useState({
    category: 'all',
    subCategory: 'all',
    subCategoryName: '',
    store: 'all',
    brand: 'all',
    priceRange: 'all',
    priceMin: 0,
    priceMax: null,
    searchQuery: '',
    imageSearch: null,
    showPromotions: false,
    showGeek: false,
    showPriceMatchOnly: false,
  });
  const [imageAnalysisData, setImageAnalysisData] = useState(null);
  const [imageSearchAnalyzing, setImageSearchAnalyzing] = useState(false);
  const [matchingProducts, setMatchingProducts] = useState(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackModalSource, setFeedbackModalSource] = useState('modal');
  const [feedbackInitialScore, setFeedbackInitialScore] = useState(0);
  const [inquiryModalProduct, setInquiryModalProduct] = useState(null);

  useEffect(() => {
    api.get('content/product-sections').then((res) => {
      const data = res.data || res;
      const all = data.allProducts;
      if (all) {
        setAllProductsConfig({
          displayCount: all.displayCount ?? 20,
          columnsPerRow: all.columnsPerRow ?? null,
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filters.imageSearch || location.pathname !== '/') return;
    document.documentElement.classList.remove('mobile-search-active');
    scrollToImageSearchView('auto');
  }, [filters.imageSearch, location.pathname]);

  useEffect(() => {
    if (!filters.imageSearch || imageSearchAnalyzing || location.pathname !== '/') return;
    scrollToImageSearchView('smooth');
  }, [filters.imageSearch, imageSearchAnalyzing, location.pathname]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('raheasy_cart');
      const parsed = savedCart ? JSON.parse(savedCart) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [myListItems, setMyListItems] = useState(() => loadMyList());
  const [listToast, setListToast] = useState({ visible: false, anchor: null, message: null });
  const [compareItems, setCompareItems] = useState(() =>
    shouldKeepCompareEmpty() ? [] : loadCompareList()
  );
  const myListProductIds = useMemo(
    () => createListProductIdSet(myListItems),
    [myListItems]
  );

  useEffect(() => {
    debouncedSyncSavedDeals(myListItems);
  }, [myListItems]);
  const compareProductIds = useMemo(
    () => createCompareProductIdSet(compareItems),
    [compareItems]
  );

  useEffect(() => {
    if (location.pathname !== '/compare' || !products.length) return;
    if (shouldKeepCompareEmpty()) return;
    const urlIds = parseCompareIdsParam(location.search);
    if (!urlIds.length) return;
    clearCompareClearedFlag();
    setCompareItems((prev) => {
      const fromUrl = buildCompareListFromIds(urlIds, products);
      const merged = mergeCompareLists(fromUrl, prev);
      const sameLength = merged.length === prev.length;
      const sameIds =
        sameLength &&
        merged.every(
          (item, index) =>
            getCompareProductId(item) === getCompareProductId(prev[index])
        );
      if (sameIds) return prev;
      saveCompareList(merged);
      debouncedSaveCompare(merged);
      return merged;
    });
  }, [location.pathname, location.search, products]);

  const savedShareHandledRef = useRef('');
  useEffect(() => {
    if (location.pathname !== '/saved') return;
    const ids = parseSavedDealIdsParam(location.search);
    const handleKey = `${location.search}`;
    if (savedShareHandledRef.current === handleKey) return;
    if (ids.length && !products.length) return;

    savedShareHandledRef.current = handleKey;
    if (ids.length) {
      setMyListItems((prev) => {
        const { list, addedCount } = mergeSharedSavedDeals(prev, ids, products);
        if (addedCount > 0) {
          saveMyList(list);
          window.setTimeout(() => {
            setListToast({
              visible: true,
              anchor: null,
              message: addedCount === 1 ? 'Added 1 shared deal' : `Added ${addedCount} shared deals`,
            });
          }, 0);
        }
        return addedCount > 0 ? list : prev;
      });
    }
    setShowOrders(true);
    navigate('/', { replace: true });
  }, [location.pathname, location.search, products, navigate]);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!getParsedUser());
  const [userInfo, setUserInfo] = useState(() => userInfoFromStorage());

  // Session lifecycle: init once, flush time-on-page on hide/close, end session on unload
  useEffect(() => {
    initSession();
    return setupSessionLifecycle();
  }, []);

  // Capture ?ref= referral codes from landing URLs
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    captureReferralFromUrl(params, getSessionId());
  }, [location.search]);

  // Track page view + time-on-page on every route change
  useEffect(() => {
    trackPageView(location.pathname);
    trackPageEnter(location.pathname);
  }, [location.pathname]);

  // Sync compare list with the logged-in account (on login and when session is restored).
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const merged = await syncCompareWithAccount();
        if (cancelled) return;
        setCompareItems((prev) => {
          const sameLength = prev.length === merged.length;
          const sameIds =
            sameLength &&
            prev.every(
              (item, index) =>
                getCompareProductId(item) === getCompareProductId(merged[index])
            );
          return sameIds ? prev : merged;
        });
      } catch (error) {
        console.error('Error syncing compare list:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // Save cart to backend when cart items change
  useEffect(() => {
    if (cartItems.length > 0) {
      debouncedSaveCart(cartItems);
    }
  }, [cartItems]);

  // Fetch notifications and calculate unread count
  const fetchNotificationCount = async () => {
    if (!getParsedUser()) {
      setUnreadNotificationCount(0);
      return;
    }

    try {
      const isLoggedIn = !!getParsedUser();
      
      // Fetch active notifications
      const response = await api.get('/notifications/active');
      
      let notificationsData = [];
      if (Array.isArray(response)) {
        notificationsData = response;
      } else if (response.data && Array.isArray(response.data)) {
        notificationsData = response.data;
      } else if (response.notifications && Array.isArray(response.notifications)) {
        notificationsData = response.notifications;
      }

      // Filter by target audience
      const filteredNotifications = notificationsData.filter(notif => {
        if (notif.targetAudience === 'all') return true;
        if (notif.targetAudience === 'registered' && isLoggedIn) return true;
        if (notif.targetAudience === 'guests' && !isLoggedIn) return true;
        return false;
      });

      // Get read status from localStorage
      const getStoredReadStatus = () => {
        try {
          const stored = localStorage.getItem('raheasy_notifications_read');
          return stored ? JSON.parse(stored) : {};
        } catch (e) {
          return {};
        }
      };

      const readStatus = getStoredReadStatus();
      
      // Calculate unread count
      const unreadCount = filteredNotifications.filter(notif => {
        const notifId = notif._id || notif.id;
        if (readStatus[notifId] === false) return false; // explicitly marked as read
        return true; // unread by default
      }).length;

      setUnreadNotificationCount(unreadCount);
    } catch (error) {
      console.error('Error fetching notification count:', error);
      // Set to 0 on error
      setUnreadNotificationCount(0);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setUnreadNotificationCount(0);
      return undefined;
    }

    fetchNotificationCount();
    const interval = setInterval(() => {
      fetchNotificationCount();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const openFeedbackModal = useCallback((source, initialScore = 0) => {
    setFeedbackModalSource(source);
    const score =
      initialScore >= 1 && initialScore <= 5 ? initialScore : 0;
    setFeedbackInitialScore(score);
    setFeedbackModalOpen(true);
  }, []);

  const openFeedbackModalIfEligible = useCallback((source, initialScore = 0, force = false) => {
    if (force) {
      openFeedbackModal(source, initialScore);
      return;
    }
    checkFeedbackEligible().then(({ canSubmit }) => {
      if (canSubmit) {
        openFeedbackModal(source, initialScore);
      }
    });
  }, [openFeedbackModal]);

  const closeFeedbackModal = () => {
    setFeedbackModalOpen(false);
    setFeedbackInitialScore(0);
  };

  useEffect(() => {
    const onPrompt = (e) => {
      openFeedbackModalIfEligible(
        e.detail?.source || 'modal',
        e.detail?.initialScore,
        e.detail?.force === true
      );
    };
    window.addEventListener('raheasy:feedback-prompt', onPrompt);
    const pending = consumePendingFeedbackSource();
    if (pending) {
      const pendingScore = consumePendingFeedbackScore();
      const t = setTimeout(
        () => openFeedbackModalIfEligible(pending, pendingScore),
        2000
      );
      return () => {
        clearTimeout(t);
        window.removeEventListener('raheasy:feedback-prompt', onPrompt);
      };
    }
    return () => window.removeEventListener('raheasy:feedback-prompt', onPrompt);
  }, [openFeedbackModalIfEligible]);

  // Refresh when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotificationCount();
        const pending = consumePendingFeedbackSource();
        if (pending) {
          const pendingScore = consumePendingFeedbackScore();
          openFeedbackModalIfEligible(pending, pendingScore);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [openFeedbackModalIfEligible]);

  useEffect(() => {
    const SESSION_START = 'raheasy_session_start_at';
    const PAGE_COUNT = 'raheasy_session_page_count';
    const FIRED = 'raheasy_session_feedback_fired';
    if (!sessionStorage.getItem(SESSION_START)) {
      sessionStorage.setItem(SESSION_START, String(Date.now()));
      sessionStorage.setItem(PAGE_COUNT, '1');
    } else {
      const c = parseInt(sessionStorage.getItem(PAGE_COUNT) || '1', 10);
      sessionStorage.setItem(PAGE_COUNT, String(c + 1));
    }
    const maybeSessionEndPrompt = () => {
      if (sessionStorage.getItem(FIRED)) return;
      const start = parseInt(sessionStorage.getItem(SESSION_START), 10);
      const pages = parseInt(sessionStorage.getItem(PAGE_COUNT) || '0', 10);
      if (!Number.isFinite(start)) return;
      if (Date.now() - start >= 30 * 60 * 1000 && pages >= 3) {
        sessionStorage.setItem(FIRED, '1');
        window.dispatchEvent(
          new CustomEvent('raheasy:feedback-prompt', { detail: { source: 'session_end' } })
        );
      }
    };
    const interval = setInterval(maybeSessionEndPrompt, 60 * 1000);
    maybeSessionEndPrompt();
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      // Track search if search query changed
      if (newFilters.searchQuery && newFilters.searchQuery !== prev.searchQuery) {
        trackSearch(newFilters.searchQuery);
      }
      return updated;
    });
  };

  // Search: site-wide — clear category scope so products in other categories can appear
  const handleSearch = (query) => {
    if (location.pathname !== '/') {
      navigate('/');
    }
    handleFilterChange({ 
      searchQuery: query != null ? String(query) : '', 
      category: 'all',
      imageSearch: null,
      subCategory: 'all',
      subCategoryName: '',
      store: 'all',
      brand: 'all',
    });
  };

  const handleImageSearch = async (imageData, file) => {
    if (location.pathname !== '/') {
      navigate('/');
    }
    setImageAnalysisData(null);
    setMatchingProducts(null);
    setImageSearchAnalyzing(true);

    handleFilterChange({
      searchQuery: '',
      imageSearch: imageData,
      category: 'all',
      subCategory: 'all',
      subCategoryName: '',
      store: 'all',
      brand: 'all',
      priceRange: 'all',
      priceMin: 0,
      priceMax: null,
    });

    let searchQuery = '';
    try {
      const catalogContext = buildImageSearchCatalogContext(categories, subcategoriesByCategory);
      const analysisResult = await analyzeImage(file, {
        catalogContext: catalogContext || undefined,
      });
      if (analysisResult.success && analysisResult.data) {
        setImageAnalysisData(analysisResult);
        const backendMatches = analysisResult.matchingProducts && analysisResult.matchingProducts.length > 0
          ? analysisResult.matchingProducts
          : searchMatchingProducts(analysisResult.data, products || []);
        setMatchingProducts(backendMatches);
        if (analysisResult.data.productName) {
          searchQuery = analysisResult.data.productName;
        } else if (analysisResult.data.keywords && analysisResult.data.keywords.length > 0) {
          searchQuery = analysisResult.data.keywords[0];
        }
      } else {
        setImageAnalysisData(analysisResult.data ? { success: false, data: analysisResult.data } : null);
        setMatchingProducts([]);
      }
    } catch (_) {
      setImageAnalysisData(null);
      setMatchingProducts([]);
    } finally {
      setImageSearchAnalyzing(false);
    }

    if (searchQuery) {
      handleFilterChange({
        searchQuery,
        imageSearch: imageData,
        category: 'all',
        subCategory: 'all',
        subCategoryName: '',
        store: 'all',
        brand: 'all',
        priceRange: 'all',
        priceMin: 0,
        priceMax: null,
      });
    }
  };

  const handleCategorySelect = (categoryId) => {
    const id = categoryId != null ? String(categoryId) : 'all';
    if (location.pathname !== '/') {
      navigate('/');
    }
    handleFilterChange({ category: id, subCategory: 'all', subCategoryName: '', store: 'all', brand: 'all', searchQuery: '', imageSearch: null });
    // Update URL so hash reflects category view (not #weekly from a previous section click)
    const path = '/';
    const hash = id !== 'all' ? '#category' : '';
    window.history.replaceState({}, document.title, hash ? `${path}${hash}` : path);
    setTimeout(() => {
      const productsSection = document.getElementById('products-section');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, location.pathname !== '/' ? 400 : 100);
  };

  // Scroll to home sections when hash is set (e.g. Featured Deals → #weekly)
  useEffect(() => {
    if (location.pathname !== '/') return;
    const hash = location.hash.replace('#', '').trim();
    if (!hash) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  // Handle legacy ?category= query parameter: redirect to clean /category/:slug URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryId = urlParams.get('category');
    if (categoryId && location.pathname === '/') {
      const cat = categories.find(
        (c) => String(c._id || c.id) === String(categoryId)
      );
      if (cat) {
        const slug = cat.slug || String(cat.name || '').toLowerCase().replace(/\s+/g, '-');
        navigate(`/category/${slug}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [location, categories, navigate]);

  const catalogMaxPrice = getCatalogMaxPrice(
    filterProductsForPriceSlider(products, filters, categories)
  );
  const priceFilterActive = isPriceSliderActive(filters, catalogMaxPrice);

  const showSidebar = filters.searchQuery || filters.category !== 'all' || filters.subCategory !== 'all' || filters.store !== 'all' || filters.brand !== 'all' || filters.priceRange !== 'all' || priceFilterActive || filters.imageSearch || filters.showPromotions || filters.showGeek;

  const notifySalesHold = () => {
    window.alert(SALES_HOLD_MESSAGE);
  };

  const handleAddToList = useCallback((product, anchor = null) => {
    if (!product) return;
    const productId = getListProductId(product);
    if (!productId || myListProductIds.has(productId)) return;
    setMyListItems((prev) => {
      const next = addProductToList(prev, product);
      saveMyList(next);
      return next;
    });
    setListToast({ visible: true, anchor, message: null });
  }, [myListProductIds]);

  const handleDismissListToast = useCallback(() => {
    setListToast({ visible: false, anchor: null, message: null });
  }, []);

  const handleViewListFromToast = useCallback(() => {
    setListToast({ visible: false, anchor: null, message: null });
    setShowOrders(true);
  }, []);

  const handleAddToCompare = useCallback((productOrProducts, anchor = null) => {
    const products = (Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts]).filter(Boolean);
    if (!products.length) return;

    const { list: next, addedCount } = addProductsToCompare(compareItems, products);
    if (addedCount === 0) {
      if (compareItems.length >= MAX_COMPARE_PRODUCTS) {
        setListToast({ visible: true, anchor, message: 'Compare full (4 max)' });
      }
      return;
    }

    setCompareItems(next);
    saveCompareList(next);
    debouncedSaveCompare(next);
    setListToast({
      visible: true,
      anchor,
      message: addedCount > 1 ? `Added ${addedCount} offers to compare!` : 'Added to compare!',
    });
  }, [compareItems]);

  const handleRemoveFromCompare = useCallback((productId) => {
    setCompareItems((prev) => {
      const next = removeProductFromCompare(prev, productId);
      if (next.length === 0) {
        persistCompareNow([]);
      } else {
        saveCompareList(next);
        debouncedSaveCompare(next);
      }
      return next;
    });
  }, []);

  const handleClearCompare = useCallback(async () => {
    markCompareCleared();
    saveCompareList([]);
    setCompareItems([]);
    if (parseCompareIdsParam(location.search).length) {
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
    } else if (location.pathname === '/compare') {
      navigate('/compare', { replace: true });
    }
    await persistCompareNow([]);
  }, [location.pathname, location.search, navigate]);

  const handleAddAllToList = useCallback((productsToAdd = []) => {
    productsToAdd.forEach((product) => {
      handleAddToList(product);
    });
  }, [handleAddToList]);

  const handleRemoveFromList = (productId) => {
    setMyListItems((prev) => {
      const next = removeProductFromList(prev, productId);
      saveMyList(next);
      return next;
    });
  };

  const handleAddToCart = (product) => {
    if (CART_DISABLED && location.pathname === '/') {
      notifySalesHold();
      return;
    }
    setCartItems(prev => {
      const existingItem = prev.find(item => item.id === product.id);
      const newCart = existingItem
        ? prev.map(item =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        : [...prev, { ...product, quantity: 1 }];
      
      // Save to backend
      debouncedSaveCart(newCart);
      // Also save to localStorage for persistence
      localStorage.setItem('raheasy_cart', JSON.stringify(newCart));
      
      return newCart;
    });
    setShowCart(true);
  };

  const finalizeAvailabilityCheck = useCallback(async (product, { email = '', surveyConsent = false, skipConfirmation = false } = {}) => {
    if (!product) return;

    const typedEmail = String(email || '').trim().toLowerCase();
    const whatsappUrl = buildAvailabilityCheckUrl(product, { template: availabilityMessageTemplate });
    const recorded = await recordAvailabilityInquiry({
      product,
      email: typedEmail,
      surveyConsent,
    });
    const inquiry = recorded?.inquiry
      ? { ...recorded.inquiry, whatsappUrl }
      : null;

    if (inquiry) {
      saveInquiryForConfirmation(inquiry);
    }

    if (showInquiryOptIn && !skipConfirmation && inquiry) {
      setInquiryModalProduct(null);
      navigate('/inquiry-confirmed', { state: { inquiry, whatsappUrl } });
      return;
    }

    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }, [availabilityMessageTemplate, navigate, showInquiryOptIn]);

  /** Check availability: optional inquiry step, then vendor chat with pre-filled message. */
  const handleCheckAvailability = useCallback(async (product) => {
    if (!product) return;

    if (showInquiryOptIn) {
      if (isLoggedIn && userInfo?.email) {
        const hasStoredConsent =
          Boolean(userInfo.googleSurveyConsent || userInfo.marketingConsent);
        const hasPriorConsent = hasStoredConsent
          ? true
          : Boolean((await fetchSurveyConsentStatus(userInfo.email))?.hasSurveyConsent);

        if (hasStoredConsent || hasPriorConsent) {
          finalizeAvailabilityCheck(product, {
            email: userInfo.email,
            surveyConsent: true,
            skipConfirmation: true,
          });
          return;
        }
      }

      setInquiryModalProduct(product);
      return;
    }

    finalizeAvailabilityCheck(product, { skipConfirmation: true });
  }, [finalizeAvailabilityCheck, showInquiryOptIn, isLoggedIn, userInfo]);

  const handleInquiryModalContinue = useCallback(({ email, surveyConsent }) => {
    finalizeAvailabilityCheck(inquiryModalProduct, { email, surveyConsent, skipConfirmation: false });
    if (surveyConsent && userInfo?.email) {
      const next = { ...getParsedUser(), googleSurveyConsent: true };
      persistAuthSession(next, Boolean(localStorage.getItem('raheasy_user')));
      setUserInfo(userInfoFromStorage());
    }
  }, [finalizeAvailabilityCheck, inquiryModalProduct, userInfo?.email]);

  const handleInquiryModalSkip = useCallback(() => {
    finalizeAvailabilityCheck(inquiryModalProduct, {
      skipConfirmation: true,
    });
  }, [finalizeAvailabilityCheck, inquiryModalProduct]);

  const handleUpdateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      handleRemoveFromCart(productId);
      return;
    }
    setCartItems(prev => {
      const newCart = prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      );
      // Save to backend
      debouncedSaveCart(newCart);
      // Also save to localStorage
      localStorage.setItem('raheasy_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  const handleRemoveFromCart = (productId) => {
    setCartItems(prev => {
      const newCart = prev.filter(item => item.id !== productId);
      // Save to backend
      debouncedSaveCart(newCart);
      // Also save to localStorage
      localStorage.setItem('raheasy_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  const handleCheckout = () => {
    // Don't require login for checkout (guest checkout allowed)
    setShowCart(false);
    // Navigate to checkout page
    // Cart is already saved in localStorage, so checkout page will read from there
    navigate('/checkout');
  };

  const handleLogin = async (credentials) => {
    const rememberMe = credentials.rememberMe !== false;
    const userPayload = {
      email: credentials.email,
      name: credentials.name || credentials.username || '',
      phone: credentials.phone || userInfo?.phone || '',
      picture: credentials.picture || userInfo?.picture || '',
      method: credentials.method || 'email',
      id: credentials.id || credentials.userId || userInfo?.id,
      token: credentials.token,
      googleSurveyConsent: Boolean(credentials.googleSurveyConsent),
      marketingConsent: Boolean(credentials.marketingConsent),
    };
    persistAuthSession(userPayload, rememberMe);
    setUserInfo(userInfoFromStorage());
    setIsLoggedIn(true);
    setShowLogin(false);

    // Load user's cart from backend
    const userId = userPayload.id || credentials.id || credentials.userId;
    if (userId) {
      try {
        const userCart = await loadUserCart();
        if (userCart && userCart.length > 0) {
          // Merge with existing cart (prefer user's saved cart)
          setCartItems(userCart);
          localStorage.setItem('raheasy_cart', JSON.stringify(userCart));
        }

        // Compare list sync runs via isLoggedIn effect (merge local + account, POST /api/compare)
        migrateGuestHistoryToUser(userId);
      } catch (error) {
        console.error('Error loading user cart/history:', error);
      }
    }
    // Removed success alert messages - login happens silently
  };

  const handleLogout = () => {
    clearAuthSession();
    setUserInfo(null);
    setIsLoggedIn(false);
    setShowNotifications(false);
    // Logout happens silently - no alert message
  };

  const handlePromotionClick = () => {
    // Filter to show only products with discounts (promotional items)
    handleFilterChange({ 
      category: 'all',
      subCategory: 'all',
      store: 'all',
      brand: 'all',
      priceRange: 'all',
      priceMin: 0,
      priceMax: null,
      searchQuery: '',
      imageSearch: null,
      showPromotions: true,
      showGeek: false,
      showPriceMatchOnly: false,
    });
    // Scroll to products section
    setTimeout(() => {
      const productsSection = document.getElementById('products-section');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleGoHome = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();

    setImageAnalysisData(null);
    setMatchingProducts(null);
    setImageSearchAnalyzing(false);
    document.documentElement.classList.remove('mobile-search-active');

    setFilters({
      category: 'all',
      subCategory: 'all',
      subCategoryName: '',
      store: 'all',
      brand: 'all',
      priceRange: 'all',
      priceMin: 0,
      priceMax: null,
      searchQuery: '',
      imageSearch: null,
      showPromotions: false,
      showGeek: false,
      showPriceMatchOnly: false,
    });

    if (location.pathname !== '/' || location.search || location.hash) {
      navigate('/', { replace: true });
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, location.search, location.hash, navigate]);


  const headerSharedProps = {
    onSearch: handleSearch,
    onImageSearch: handleImageSearch,
    onHomeClick: handleGoHome,
    searchQuery: filters.searchQuery,
    onNotificationsClick: () => setShowNotifications(true),
    onLoginClick: () => setShowLogin(true),
    onOrdersClick: () => setShowOrders(true),
    onCartClick: () => setShowCart(true),
    cartItemCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
    unreadNotificationCount,
    imageSearchActive: !!filters.imageSearch,
    isLoggedIn,
    userInfo,
    onLogout: handleLogout,
    hideCart: CART_DISABLED,
    myListItemCount: myListItems.length,
  };

  return (
    <>
      <ScrollToTop />
      <PageSeoManager />
      <Suspense fallback={<div className="route-loading" aria-live="polite">Loading…</div>}>
      <Routes>
        <Route path="/r/:code" element={<ReferralRedirect sessionId={getSessionId()} />} />
        <Route path="/about" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <About />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/partner-with-us" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <PartnerWithUs />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/meta-oembed-demo" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <MetaOEmbedDemo />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/help" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <HelpCentre />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        {/* Delivery page commented out – service not offered currently; uncomment route and import when ready
        <Route path="/delivery" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <Delivery />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        */}
        <Route path="/warranty" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <Blog />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/blog" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <Blog />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/blog/:slug" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <BlogArticle />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/reset-password" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <ResetPassword />
            <Footer />
            <FloatingButton />
            <LoginModal 
              isOpen={showLogin} 
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              isLoggedIn={isLoggedIn}
              userInfo={userInfo}
            />
          </>
        } />
        <Route path="/email/unsubscribe" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <EmailUnsubscribe />
            <Footer />
            <FloatingButton />
          </>
        } />
        <Route path="/privacy-policy" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <PrivacyPolicy />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/checkout" element={<CheckoutWrapper />} />
        <Route path="/order-confirmation" element={<OrderConfirmationWrapper />} />
        <Route path="/inquiry-confirmed" element={<InquiryConfirmedWrapper />} />
        <Route path="/product-request" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <ProductRequest />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/outlets/hotpoint/price-match" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <HotpointPriceMatchPage
              onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            <Footer />
            <FloatingButton />
            <Notifications
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <LoginModal
              isOpen={showLogin}
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              isLoggedIn={isLoggedIn}
              userInfo={userInfo}
            />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
          </>
        } />
        <Route path="/outlets/:vendorId" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <VendorProducts onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList} />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/category/:categorySlug" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <CategoryPage onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds} onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList} />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/category/:categorySlug/:subSlug" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <CategoryPage onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds} onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList} />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/outlets" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <Outlets />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route
          path="/hotpoint/price-match"
          element={<Navigate to="/outlets/hotpoint/price-match" replace />}
        />
        <Route path="/deals" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <AllDealsPage
              onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
              displayCount={allProductsConfig.displayCount}
              columnsPerRow={allProductsConfig.columnsPerRow}
            />
            <Footer />
            <FloatingButton />
            <Notifications
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <LoginModal
              isOpen={showLogin}
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              isLoggedIn={isLoggedIn}
              userInfo={userInfo}
            />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
          </>
        } />
        <Route path="/ending-soon" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <EndingSoonPage
              onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            <Footer />
            <FloatingButton />
            <Notifications
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <LoginModal
              isOpen={showLogin}
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              isLoggedIn={isLoggedIn}
              userInfo={userInfo}
            />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
          </>
        } />
        <Route path="/new-deals" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <NewDealsPage
              onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            <Footer />
            <FloatingButton />
            <Notifications
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <LoginModal
              isOpen={showLogin}
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              isLoggedIn={isLoggedIn}
              userInfo={userInfo}
            />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
          </>
        } />
        <Route path="/compare" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <CompareProducts
              compareItems={compareItems}
              onRemoveFromCompare={handleRemoveFromCompare}
              onClearCompare={handleClearCompare}
              onAddToList={handleAddToList}
              onAddAllToList={handleAddAllToList}
              listProductIds={myListProductIds}
            />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <LoginModal 
              isOpen={showLogin} 
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              isLoggedIn={isLoggedIn}
              userInfo={userInfo}
            />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
          </>
        } />
        <Route path="/product/:id" element={
          <>
            <TopBar />
            <Header {...headerSharedProps} />
            <ProductDetail onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList} />
            <Footer />
            <FloatingButton />
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </>
        } />
        <Route path="/saved" element={
          <div className="route-loading" aria-live="polite">Opening saved deals…</div>
        } />
        <Route path="/" element={
          <div className="App" data-catalog-ready={initialCatalogLoaded ? 'true' : 'false'}>
            <TopBar />
            <Header {...headerSharedProps} />
            <PromoBanner onShowPromotions={handlePromotionClick} />
            
            {/* Sidebar and ProductGrid when filters are active - positioned early */}
            {showSidebar && (
                <div className="products-page-layout" id="products-section">
                  <div className="products-page-layout-inner">
                    <Sidebar filters={filters} onFilterChange={handleFilterChange} />
                    <div className="products-main-column">
                      {filters.imageSearch && (
                        <ImageSearchResults
                          imageData={filters.imageSearch}
                          searchQuery={filters.searchQuery || 'product'}
                          onAddToList={handleAddToList}
                          listProductIds={myListProductIds}
                          onAddToCompare={handleAddToCompare}
                          compareProductIds={compareProductIds}
                          onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
                          onClear={() => {
                            handleFilterChange({ imageSearch: null, searchQuery: '' });
                            setImageAnalysisData(null);
                            setMatchingProducts(null);
                            setImageSearchAnalyzing(false);
                          }}
                          onRefineSearch={(term) => {
                            handleFilterChange({ searchQuery: term || '', imageSearch: null });
                            setImageAnalysisData(null);
                            setMatchingProducts(null);
                            setImageSearchAnalyzing(false);
                          }}
                          analysisData={imageAnalysisData}
                          matchingProducts={matchingProducts}
                          isAnalyzing={imageSearchAnalyzing}
                        />
                      )}
                      <ProductGrid 
                        hideHeaderTitle={!!filters.imageSearch}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        showFullWidth={false}
                        onAddToList={handleAddToList}
                        listProductIds={myListProductIds}
                        onAddToCompare={handleAddToCompare}
                        compareProductIds={compareProductIds}
                        onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
                      />
                    </div>
                  </div>
                </div>
            )}
            
            {/* Banner Section */}
            {!showSidebar && <Banner onPromotionClick={handlePromotionClick} />}

            {/* Home metrics strip (under banner) */}
            {!showSidebar && <StatsStrip />}
            
            {/* Category Grid Section */}
            {!showSidebar && (
              <CategoryGrid onCategorySelect={handleCategorySelect} />
            )}


            {/* Product Sections: Best Sellers, Weekly Promotion, etc. */}
            {!showSidebar && (
              <ProductSections 
                onCategorySelect={handleCategorySelect}
                onAddToList={handleAddToList}
              listProductIds={myListProductIds}
              onAddToCompare={handleAddToCompare}
              compareProductIds={compareProductIds}
                onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
              />
            )}

            {/* Social Media Feed Section – always visible so social cards are shown */}
            <SocialFeed />

            <Footer />
            <FloatingButton />
            {/* Modals */}
            <Notifications 
              isOpen={showNotifications} 
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={setUnreadNotificationCount}
            />
                     <LoginModal 
                       isOpen={showLogin} 
                       onClose={() => setShowLogin(false)}
                       onLogin={handleLogin}
                       isLoggedIn={isLoggedIn}
                       userInfo={userInfo}
                     />
            <MyListModal
              isOpen={showOrders}
              onClose={() => setShowOrders(false)}
              items={myListItems}
              onRemoveItem={handleRemoveFromList}
              onCheckAvailability={handleCheckAvailability}
              onRemoveFromList={handleRemoveFromList}
            />
            {!CART_DISABLED && (
            <ShoppingCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateCartQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleCheckout}
            />
            )}
          </div>
        } />
      </Routes>
      </Suspense>
      {process.env.NODE_ENV === 'development' ? <ApiDiagnostics /> : null}
      <PlatformExperienceModal
        open={feedbackModalOpen}
        source={feedbackModalSource}
        initialScore={feedbackInitialScore}
        onClose={closeFeedbackModal}
      />
      <ListToast
        visible={listToast.visible}
        anchor={listToast.anchor}
        message={listToast.message}
        onClose={handleDismissListToast}
        onViewList={listToast.message ? undefined : handleViewListFromToast}
      />
      {compareItems.length > 0 && location.pathname !== '/compare' && (
        <CompareBar
          items={compareItems}
          onClear={handleClearCompare}
          onRemoveItem={handleRemoveFromCompare}
        />
      )}
      <FloatingReviewPopup />
      <AvailabilityInquiryModal
        open={Boolean(inquiryModalProduct)}
        product={inquiryModalProduct}
        defaultEmail={userInfo?.email || ''}
        onClose={() => setInquiryModalProduct(null)}
        onContinue={handleInquiryModalContinue}
        onSkip={handleInquiryModalSkip}
      />
    </>
  );
}

function App() {
  const enableVercelAnalytics = process.env.REACT_APP_ENABLE_VERCEL_ANALYTICS === 'true';

  return (
    <Router>
      <StorefrontSettingsProvider>
        <AppContent />
      </StorefrontSettingsProvider>
      {enableVercelAnalytics && <Analytics />}
      <SpeedInsights />
    </Router>
  );
}

export default App;

